<script setup>
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { Teleport, ref, watch, nextTick } from 'vue';
import { provideDropdownContext } from './provider.js';

const props = defineProps({
  usePortal: {
    type: Boolean,
    default: false,
  },
  portalPosition: {
    type: String,
    default: 'bottom', // 'bottom', 'top', 'left', 'right'
  },
});

const emit = defineEmits(['close']);
const [isOpen, toggle] = useToggle(false);
const triggerRef = ref(null);
const portalPosition = ref({ top: 0, left: 0 });

const updatePortalPosition = () => {
  if (!triggerRef.value || !props.usePortal) return;
  const rect = triggerRef.value.getBoundingClientRect();
  const isRTL = document.documentElement.dir === 'rtl';

  // Position par défaut: en dessous du trigger, aligné à gauche (ou droite en RTL)
  // Avec position: fixed, les coordonnées sont relatives au viewport
  let top = rect.bottom + 4; // 4px offset
  let left = rect.left;

  if (props.portalPosition === 'top') {
    // Positionner au-dessus du trigger
    // Le dropdown sera positionné avec son bas au niveau du haut du trigger moins 4px
    // On utilisera transform: translateY(-100%) dans le DropdownBody pour le positionner correctement
    top = rect.top - 4; // Positionner le haut du dropdown 4px au-dessus du haut du trigger
  }

  // Pour RTL, aligner à droite si nécessaire
  if (isRTL && props.portalPosition !== 'top') {
    // Le positionnement sera géré par les classes CSS du DropdownBody
  }

  portalPosition.value = { top, left };
};

watch(isOpen, async newValue => {
  if (newValue && props.usePortal) {
    await nextTick();
    updatePortalPosition();
  }
});

const closeMenu = () => {
  if (isOpen.value) {
    emit('close');
    toggle(false);
  }
};

provideDropdownContext({
  isOpen,
  toggle,
  closeMenu,
});
</script>

<template>
  <div v-on-click-outside="closeMenu" class="relative space-y-2">
    <div ref="triggerRef">
      <slot name="trigger" :is-open :toggle="() => toggle()" />
    </div>
    <template v-if="isOpen">
      <Teleport v-if="usePortal" to="body">
        <div
          class="fixed z-[9999]"
          :style="{
            top: `${portalPosition.top}px`,
            left: `${portalPosition.left}px`,
          }"
        >
          <slot />
        </div>
      </Teleport>
      <div v-else class="absolute z-[9999]">
        <slot />
      </div>
    </template>
  </div>
</template>
